import { Download, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrg } from '@/admin/hooks/use-org'
import api from '@/admin/lib/api'
import { toastApiError } from '@/shared/lib/api-error'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  usePartnershipDuplicates,
  type DuplicateGroup,
} from '@/admin/hooks/use-partnership-health'

/**
 * Possible duplicate dealer records, with the evidence a person needs.
 *
 * Read-only on purpose. Detection suggests, it never merges: three similarity
 * heuristics were tried during the migration and each was wrong in a different
 * direction, so the account codes and email domains go on screen and a human
 * decides. Merging arrives as its own flow later.
 */

const BUCKET_VARIANT: Record<string, 'default' | 'secondary' | 'destructive'> = {
  clear: 'default',
  needs_review: 'secondary',
  blocked: 'destructive',
}

// Readable fallbacks, so a missing translation degrades to English rather than
// showing the operator a raw enum value like "needs_review".
const BUCKET_FALLBACK: Record<string, string> = {
  clear: 'Clear',
  needs_review: 'Needs a look',
  blocked: 'Cannot merge',
}

function GroupRow({
  group,
  onOpenPartner,
  onReviewGroup,
}: {
  group: DuplicateGroup
  onOpenPartner: (uuid: string) => void
  onReviewGroup?: (group: DuplicateGroup) => void
}) {
  const { t } = useTranslation()
  const bucketLabel = t(`network.duplicates.bucket.${group.bucket}`, {
    defaultValue: BUCKET_FALLBACK[group.bucket] ?? group.bucket,
  })

  return (
    <div
      className="border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-4 last:border-b-0"
      data-testid="duplicate-group"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] text-[var(--ink)]">{group.display_name}</span>
        <Badge variant={BUCKET_VARIANT[group.bucket] ?? 'secondary'}>
          {bucketLabel}
        </Badge>
        <span className="clara-small">
          {t('network.duplicates.summary', {
            records: group.record_count,
            registrations: group.registration_count,
            defaultValue: '{{records}} records - {{registrations}} registrations',
          })}
        </span>
        {/* The queue's whole purpose is to be worked through. Without an action
            the operator has to memorise a name, leave, find the record, and
            rebuild the selection the scan already computed. */}
        {onReviewGroup && group.suggested_survivor_uuid && (
          <Button
            variant="outline"
            size="xs"
            data-testid="duplicate-group-review"
            className="ml-auto"
            onClick={() => onReviewGroup(group)}
          >
            {t('network.duplicates.review', 'Review and merge')}
          </Button>
        )}
      </div>

      {group.account_numbers.length > 0 && (
        <p className="clara-small mt-2">
          {t('network.duplicates.accounts', 'Account codes')}:{' '}
          {group.account_numbers.join(', ')}
        </p>
      )}
      {group.email_domains.length > 0 && (
        <p className="clara-small">
          {t('network.duplicates.domains', 'Email domains')}:{' '}
          {group.email_domains.join(', ')}
        </p>
      )}
      {group.parents_seen.length > 0 && (
        <p className="clara-small">
          {t('network.duplicates.parents', 'Grouped under')}:{' '}
          {group.parents_seen.join(', ')}
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-1">
        {group.members.map((member) => (
          <Button
            key={member.partnership_uuid ?? member.name}
            variant="outline"
            size="xs"
            onClick={() =>
              member.partnership_uuid && onOpenPartner(member.partnership_uuid)
            }
          >
            {member.name}
            {member.registration_count ? ` · ${member.registration_count}` : ''}
          </Button>
        ))}
      </div>
    </div>
  )
}

const GROUPS_PER_PAGE = 10

export function NetworkDuplicatesPanel({
  onOpenPartner,
  onReviewGroup,
}: {
  onOpenPartner: (uuid: string) => void
  onReviewGroup?: (group: DuplicateGroup) => void
}) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [exporting, setExporting] = useState(false)
  const { orgUuid } = useOrg()
  const { data, isPending } = usePartnershipDuplicates()

  if (isPending) {
    return (
      <p className="clara-small py-6" data-testid="duplicates-loading">
        {t('network.duplicates.loading', 'Loading...')}
      </p>
    )
  }

  const groups = data?.groups ?? []
  // 127 groups, each listing its records, is a page nobody reaches the bottom
  // of. Paged client-side: the endpoint already returns the whole set in one
  // request, and a group is only meaningful whole.
  const pageCount = Math.max(1, Math.ceil(groups.length / GROUPS_PER_PAGE))
  const currentPage = Math.min(page, pageCount)
  const visible = groups.slice(
    (currentPage - 1) * GROUPS_PER_PAGE,
    currentPage * GROUPS_PER_PAGE,
  )

  if (groups.length === 0) {
    return (
      <div
        className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-5 py-6"
        data-testid="duplicates-empty"
      >
        <p className="text-[13px] text-[var(--ink)]">
          {t('network.duplicates.empty', 'No possible duplicates found.')}
        </p>
      </div>
    )
  }

  return (
    <div data-testid="duplicates-panel">
      <div className="mb-4 flex items-end justify-between gap-4">
        <p className="clara-small">
          {t('network.duplicates.intro', {
            count: groups.length,
            defaultValue:
              '{{count}} sets of records may be the same business. Nothing here has been changed.',
          })}
        </p>
        {/* Whether two records are one dealership is usually a question for
            whoever owns the dealer list, not one the data can answer. This is
            the evidence in a form they can annotate and send back.

            Fetched through the api client, not window.open: auth is a bearer
            header (withCredentials is false), so a plain navigation carries no
            token and the browser would land on a 403 instead of a file. */}
        <Button
          variant="outline"
          size="sm"
          data-testid="duplicates-export"
          disabled={exporting}
          onClick={async () => {
            setExporting(true)
            try {
              const res = await api.get(
                `/organisations/${orgUuid}/partnership-health/duplicates/export/`,
                { responseType: 'blob' },
              )
              const url = URL.createObjectURL(res.data as Blob)
              const link = document.createElement('a')
              link.href = url
              link.download = 'possible-duplicate-dealers.csv'
              // In the DOM before the click, and revoked a tick after it:
              // Firefox ignores a click on a detached anchor, and Safari
              // cancels the download if the blob URL is revoked before it has
              // started reading it.
              document.body.appendChild(link)
              link.click()
              link.remove()
              setTimeout(() => URL.revokeObjectURL(url), 0)
            } catch (err) {
              toastApiError(
                err,
                t(
                  'network.duplicates.exportFailed',
                  'Could not build that export.',
                ),
              )
            } finally {
              setExporting(false)
            }
          }}
        >
          {exporting ? (
            <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
          ) : (
            <Download className="mr-1.5 h-3 w-3" />
          )}
          {t('network.duplicates.export', 'Export for review')}
        </Button>
      </div>
      <div className="rounded-lg border-[0.5px] border-[var(--rule)] overflow-hidden">
        {visible.map((group) => (
          <GroupRow
            key={group.key}
            group={group}
            onOpenPartner={onOpenPartner}
            onReviewGroup={onReviewGroup}
          />
        ))}
      </div>
      {pageCount > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="clara-small">
            {t('network.duplicates.pageOf', {
              from: (currentPage - 1) * GROUPS_PER_PAGE + 1,
              to: Math.min(currentPage * GROUPS_PER_PAGE, groups.length),
              total: groups.length,
              defaultValue: 'Showing {{from}}-{{to}} of {{total}} sets',
            })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="xs"
              data-testid="duplicates-prev"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
            >
              {t('common.previous', 'Previous')}
            </Button>
            <Button
              variant="outline"
              size="xs"
              data-testid="duplicates-next"
              disabled={currentPage >= pageCount}
              onClick={() => setPage(currentPage + 1)}
            >
              {t('common.next', 'Next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
